import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationsApi } from '../api/endpoints.ts';
import { formatShortInstant } from '../i18n/datetime.ts';
import type { AppNotification } from '@shared/types/index.ts';
import { Icon } from './icons.tsx';

/**
 * The header's bell: an unread badge that keeps itself current, and a panel
 * of the most recent notices with a road to the full page.
 *
 * The badge asks `/notifications/state` on a 30-second interval — the app
 * has no push channel and `refetchOnWindowFocus` is globally off, so without
 * a poll the count would only ever change on navigation. Thirty seconds on
 * one indexed COUNT is far below the noise floor, and `refetchInterval`
 * pauses in background tabs by default, so an office of open tabs is not a
 * heartbeat chorus.
 *
 * Reading is an act: opening the panel does not mark anything, the same rule
 * the feedback panel documents — a badge nobody has looked at is not cleared
 * by walking past it. Clicking a notice marks that one and follows its link;
 * "mark all read" is the explicit broom.
 */

/** How often the badge re-asks. One indexed COUNT per open tab per tick. */
const BADGE_POLL_MS = 30_000;

/** How many notices the dropdown shows; the page has the rest. */
const PANEL_SIZE = 8;

// One root, so everything the feature caches invalidates as one prefix —
// a future notification query added under it stays fresh for free.
const NOTIFICATION_ROOT_KEY = ['notifications'];
export const NOTIFICATION_STATE_KEY = [...NOTIFICATION_ROOT_KEY, 'state'];
export const NOTIFICATION_PANEL_KEY = [...NOTIFICATION_ROOT_KEY, 'panel'];
/** Prefix key for the full page's queries. */
export const NOTIFICATION_LIST_KEY = [...NOTIFICATION_ROOT_KEY, 'list'];

export function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_ROOT_KEY });
  };
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<{
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // Same press-in-flight guard as AccountMenu (see app-layout.tsx): Safari
  // blurs to nothing on a click, and closing on that blur would unmount the
  // very link being chosen.
  const pressInFlight = useRef(false);
  const invalidate = useInvalidateNotifications();

  const state = useQuery({
    queryKey: NOTIFICATION_STATE_KEY,
    queryFn: () => notificationsApi.state(),
    refetchInterval: BADGE_POLL_MS,
  });

  const panel = useQuery({
    queryKey: NOTIFICATION_PANEL_KEY,
    queryFn: () => notificationsApi.list({ pageSize: PANEL_SIZE }),
    enabled: open,
    // While the panel is held open, keep the list on the badge's own beat —
    // otherwise a poll tick bumps the count over a frozen list.
    refetchInterval: BADGE_POLL_MS,
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => notificationsApi.markRead(ids),
    onSettled: invalidate,
  });

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  // The panel is wide (384px) but the bell isn't guaranteed to sit near the
  // screen's right edge — on mobile the offline badge and sign-out button
  // still trail it. Anchoring purely in CSS (`right-0` on the bell's own
  // wrapper) clips the panel off the left edge in that case, so instead we
  // measure the button and clamp the panel's left edge to the viewport.
  //
  // The same problem exists vertically on a short viewport (landscape phone,
  // heavy zoom): a full panel can run ~470px tall, and a naive "always below
  // the bell" placement can push its bottom — the "View all" link included —
  // past the screen with nothing to scroll it into view, since the panel is
  // `fixed` and out of document flow. So we budget a maxHeight against
  // whichever side (below or above the button) has more room, and flip above
  // when below is too tight. That budget is never padded past what's
  // actually there — an extremely short viewport just gets a squeezed,
  // internally-scrollable panel rather than one that overflows the screen.
  //
  // The bell's own position can shift while the panel is open without a
  // resize or scroll — e.g. AppLayout mounts an offline badge right after it,
  // which can push the bell onto a new wrapped header row. Rather than chase
  // every possible cause, track the anchor every frame while open, same as
  // Floating UI's `autoUpdate` falls back to for anchors that move for
  // reasons other than scroll/resize.
  useLayoutEffect(() => {
    if (!open) return;
    let frame: number;
    const updatePosition = () => {
      const rect = button.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 12;
      const gap = 4;
      const width = Math.min(384, window.innerWidth - margin * 2);
      const left = Math.max(margin, Math.min(rect.right - width, window.innerWidth - width - margin));
      // On a phone the shell's bottom navigation bar is fixed over the last
      // strip of the screen, and it is painted over this panel; the panel's
      // floor is the bar's top edge rather than the screen's. Display:none
      // from `sm` up, where it measures zero tall and the screen is the floor.
      const bar = document.querySelector('[data-shell-nav="bottom"]')?.getBoundingClientRect();
      const floor = bar && bar.height > 0 ? Math.min(window.innerHeight, bar.top) : window.innerHeight;
      const spaceBelow = floor - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const next =
        spaceBelow >= spaceAbove
          ? { top: rect.bottom + gap, left, width, maxHeight: Math.max(0, spaceBelow) }
          : { bottom: window.innerHeight - rect.top + gap, left, width, maxHeight: Math.max(0, spaceAbove) };
      setPanelStyle((prev) =>
        prev &&
        prev.top === next.top &&
        prev.bottom === next.bottom &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.maxHeight === next.maxHeight
          ? prev
          : next,
      );
    };
    const loop = () => {
      updatePosition();
      frame = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const unread = state.data?.unread ?? 0;

  function follow(notification: AppNotification) {
    setOpen(false);
    if (!notification.readAt) markRead.mutate([notification.id]);
    if (notification.linkPath) navigate(notification.linkPath);
  }

  return (
    <div
      className="relative"
      ref={wrapper}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          setOpen(false);
          button.current?.focus();
        }
      }}
      onMouseDown={(event) => {
        if (event.button !== 0) return;
        pressInFlight.current = true;
        window.addEventListener(
          'mouseup',
          () => {
            pressInFlight.current = false;
          },
          { once: true },
        );
      }}
      onBlur={(event) => {
        if (event.relatedTarget instanceof Node && wrapper.current?.contains(event.relatedTarget)) return;
        if (pressInFlight.current) return;
        setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-label={
          unread > 0
            ? `${t('notifications.bellLabel')} — ${t('notifications.unreadCount', { count: unread })}`
            : t('notifications.bellLabel')
        }
        onClick={() => {
          // Re-ask for BOTH halves on the way open: refreshing only the
          // count would let a still-fresh cached panel contradict the badge
          // it sits under (three unread, two listed).
          if (!open) invalidate();
          setOpen(!open);
        }}
        className="relative flex h-10 w-10 items-center justify-center rounded-md text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-brand)]"
      >
        <Icon name="bell" />
        {unread > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--color-brand)] px-1 text-[10px] font-bold leading-none text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open && panelStyle ? (
        <div
          style={{
            top: panelStyle.top,
            bottom: panelStyle.bottom,
            left: panelStyle.left,
            width: panelStyle.width,
            maxHeight: panelStyle.maxHeight,
          }}
          className="fixed z-30 flex flex-col overflow-y-auto rounded-md border border-[color:var(--color-line)] bg-white shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-[color:var(--color-line)] px-3 py-2">
            <h2 className="text-sm font-semibold">{t('notifications.title')}</h2>
            {unread > 0 ? (
              <button
                type="button"
                className="text-xs font-semibold text-[color:var(--color-brand)] hover:underline disabled:opacity-50"
                disabled={markRead.isPending}
                onClick={() => markRead.mutate(undefined)}
              >
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>

          {panel.isLoading ? (
            <p className="px-3 py-4 text-sm text-[color:var(--color-muted)]">{t('notifications.loading')}</p>
          ) : null}
          {panel.isError ? (
            <p className="px-3 py-4 text-sm text-[color:var(--color-muted)]">{t('notifications.loadErrorTitle')}</p>
          ) : null}
          {panel.data && panel.data.items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-[color:var(--color-muted)]">{t('notifications.empty')}</p>
          ) : null}

          {panel.data && panel.data.items.length > 0 ? (
            <ul className="p-1">
              {panel.data.items.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => follow(notification)}
                    className="flex w-full items-start gap-2 rounded px-2 py-2 text-left hover:bg-[color:var(--color-surface)]"
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        notification.readAt ? 'bg-transparent' : 'bg-[color:var(--color-brand)]'
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {notification.title}
                        {!notification.readAt ? (
                          <span className="sr-only"> ({t('notifications.unreadMarker')})</span>
                        ) : null}
                      </span>
                      {notification.body ? (
                        <span className="block truncate text-xs text-[color:var(--color-muted)]">
                          {notification.body}
                        </span>
                      ) : null}
                      <span className="block text-xs text-[color:var(--color-muted)]">
                        {formatShortInstant(notification.createdAt)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="border-t border-[color:var(--color-line)] p-1">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block rounded px-2 py-2 text-center text-sm font-semibold text-[color:var(--color-brand)] hover:bg-[color:var(--color-surface)]"
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
